/** Matches server DEV_ADMIN_INGEST_SECRET in ingestAuth.ts */
const DEV_ADMIN_INGEST_SECRET = "dev-local-secret";

const STORAGE_KEY = "bouncer-admin-secret";

export type AdminBootstrapInfo = {
  secret: string | null;
  dbConfigured: boolean;
};

let cachedBootstrap: AdminBootstrapInfo | null = null;
let bootstrapPromise: Promise<AdminBootstrapInfo> | null = null;

export const isLoopbackHostname = (hostname: string) =>
  hostname === "localhost" || hostname === "127.0.0.1" || hostname === "::1";

export const getAdminBootstrapInfo = () => cachedBootstrap;

export const getAdminClientSecret = () => {
  if (typeof sessionStorage !== "undefined") {
    const stored = sessionStorage.getItem(STORAGE_KEY);

    if (stored) return stored;
  }

  if (typeof location !== "undefined" && isLoopbackHostname(location.hostname)) {
    return DEV_ADMIN_INGEST_SECRET;
  }

  return null;
};

export const setAdminClientSecret = (secret: string) => {
  sessionStorage.setItem(STORAGE_KEY, secret);
};

export const ensureAdminClientAuth = async (): Promise<AdminBootstrapInfo> => {
  if (bootstrapPromise) {
    return bootstrapPromise;
  }

  bootstrapPromise = (async () => {
    const existing = getAdminClientSecret();

    if (existing) {
      cachedBootstrap = {
        secret: existing,
        dbConfigured: cachedBootstrap?.dbConfigured ?? true,
      };

      return cachedBootstrap;
    }

    if (typeof location !== "undefined" && isLoopbackHostname(location.hostname)) {
      try {
        const response = await fetch("/api/admin/dev-bootstrap");

        if (response.ok) {
          const body = await response.json() as { secret?: string; dbConfigured?: boolean };

          if (body.secret) {
            setAdminClientSecret(body.secret);
            cachedBootstrap = {
              secret: body.secret,
              dbConfigured: body.dbConfigured ?? true,
            };

            return cachedBootstrap;
          }
        }
      } catch {
        // fall through to loopback default
      }

      cachedBootstrap = {
        secret: DEV_ADMIN_INGEST_SECRET,
        dbConfigured: true,
      };

      return cachedBootstrap;
    }

    cachedBootstrap = { secret: null, dbConfigured: true };

    return cachedBootstrap;
  })();

  return bootstrapPromise;
};

export const adminFetch = (input: string, init: RequestInit = {}) => {
  const secret = getAdminClientSecret();
  const headers = new Headers(init.headers);

  if (secret) {
    headers.set("X-Admin-Secret", secret);
  }

  return fetch(input, { ...init, headers });
};

export const adminLiveUrl = () => {
  const secret = getAdminClientSecret();

  if (!secret) return "/api/admin/live";

  return `/api/admin/live?secret=${encodeURIComponent(secret)}`;
};
